import { useId, useRef, useState, type MutableRefObject } from 'react';
import { autoUpdate, flip, FloatingFocusManager, FloatingPortal, offset, shift, size, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import { ArrowLeft01Icon, ArrowRight01Icon, FilterIcon, Search01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { MultiSelectFilter, type MultiSelectOption, type MultiSelectPreset } from '../shared/MultiSelectFilter';

export interface IssueFilterGroup {
  name: string;
  icon: IconSvgElement;
  idleLabel: string;
  'aria-label': string;
  options: MultiSelectOption[];
  value: readonly string[];
  defaultValue?: readonly string[];
  presets?: MultiSelectPreset[];
  resetLabel?: string;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  alwaysVisible?: boolean;
}

const isChanged = (group: IssueFilterGroup) => group.value.length > 0 && (
  group.value.length !== (group.defaultValue?.length ?? 0)
  || group.value.some(value => !group.defaultValue?.includes(value))
);

type MenuItem = { group: IssueFilterGroup; option?: MultiSelectOption };

function FilterMenu({ groups, buttonRef }: { groups: IssueFilterGroup[]; buttonRef: MutableRefObject<HTMLButtonElement | null> }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();
  const { refs, floatingStyles, context } = useFloating({
    open, onOpenChange: next => { setOpen(next); if (!next) { setCategory(null); setQuery(''); setActiveIndex(null); } },
    placement: 'bottom-start', strategy: 'fixed', whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 8 }), size({ padding: 8, apply({ availableHeight, availableWidth, elements }) {
      Object.assign(elements.floating.style, { width: `${Math.min(320, availableWidth)}px`, maxHeight: `${Math.max(0, Math.min(400, availableHeight))}px` });
    } })],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
  const selectedGroup = groups.find(group => group.name === category);
  const term = query.trim().toLocaleLowerCase();
  const items: MenuItem[] = selectedGroup
    ? selectedGroup.options.filter(option => option.label.toLocaleLowerCase().includes(term)).map(option => ({ group: selectedGroup, option }))
    : term
      ? groups.flatMap(group => group.options.filter(option => `${group.name} ${option.label}`.toLocaleLowerCase().includes(term)).map(option => ({ group, option })))
      : groups.map(group => ({ group }));
  const active = activeIndex !== null && activeIndex < items.length ? activeIndex : null;
  const appliedCount = groups.filter(isChanged).length;

  function choose(item: MenuItem) {
    if (item.group.disabled) return;
    if (item.option) {
      const value = item.option.value;
      item.group.onChange(item.group.value.includes(value) ? item.group.value.filter(selected => selected !== value) : [...item.group.value, value]);
    } else {
      setCategory(item.group.name); setQuery(''); setActiveIndex(null);
    }
    search.current?.focus({ preventScroll: true });
  }

  return <>
    <button ref={node => { refs.setReference(node); buttonRef.current = node; }} {...getReferenceProps()} type="button"
      className="input input-toolbar flex w-[var(--oq-control-app-toolbar-height)] shrink-0 items-center justify-center px-0" title="Filter" aria-haspopup="dialog" aria-expanded={open}
      aria-controls={open ? `${listId}-dialog` : undefined} aria-label={appliedCount ? `Filter, ${appliedCount} active` : 'Filter'}>
      <HugeiconsIcon icon={FilterIcon} size={14} strokeWidth={2} className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    </button>
    {open && <FloatingPortal><FloatingFocusManager context={context} modal={false} initialFocus={search}>
      <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} id={`${listId}-dialog`} role="dialog" aria-label="Issue filters"
        className="z-50 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
        {selectedGroup && <div className="flex items-center gap-2 px-2 pb-1">
          <button type="button" className="btn-ghost btn-toolbar px-2" aria-label="Back to all filters"
            onClick={() => { setCategory(null); setQuery(''); setActiveIndex(null); search.current?.focus(); }}>
            <HugeiconsIcon icon={ArrowLeft01Icon} size={14} strokeWidth={2} aria-hidden="true" />
          </button>
          <span className="oq-text-label text-gray-700">{selectedGroup.name}</span>
        </div>}
        <div className="px-2 pb-1">
          <input ref={search} type="text" role="combobox" aria-label="Search filters" aria-autocomplete="list" aria-expanded="true"
            aria-controls={listId} aria-activedescendant={active === null ? undefined : `${listId}-${active}`}
            className="input input-toolbar w-full" placeholder={selectedGroup ? `Search ${selectedGroup.name.toLowerCase()}…` : 'Search filters or values…'} value={query}
            onChange={event => { setQuery(event.target.value); setActiveIndex(null); }}
            onKeyDown={event => {
              if (event.nativeEvent.isComposing) return;
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (!items.length) return;
                const next = active === null ? (event.key === 'ArrowDown' ? 0 : items.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                setActiveIndex(next); document.getElementById(`${listId}-${next}`)?.scrollIntoView({ block: 'nearest' });
              } else if (event.key === 'Enter') {
                event.preventDefault();
                if (items[active ?? 0]) choose(items[active ?? 0]);
              }
            }} />
        </div>
        <ul id={listId} role="listbox" aria-label={selectedGroup?.name ?? 'Filters and values'} aria-multiselectable={selectedGroup || term ? true : undefined} className="min-h-0 overflow-y-auto">
          {items.map((item, index) => {
            const selected = !!item.option && item.group.value.includes(item.option.value);
            return <li key={`${item.group.name}:${item.option?.value ?? 'category'}`} id={`${listId}-${index}`} role="option" aria-selected={selected}
              aria-disabled={item.group.disabled || undefined} aria-label={item.option ? `${item.group.name}: ${item.option.label}` : item.group.name}
              onMouseDown={event => event.preventDefault()} onClick={() => choose(item)}
              className={`flex items-center gap-3 px-3 py-2 oq-text-body text-gray-700 ${item.group.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'} ${active === index ? 'bg-gray-100' : ''}`}>
              {item.option ? <>
                <span aria-hidden="true" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-300'}`}>
                  {selected && <HugeiconsIcon icon={Tick02Icon} size={12} strokeWidth={3} className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1 break-words">{item.option.label}</span>
                {!selectedGroup && <span className="shrink-0 text-xs text-gray-600">{item.group.name}</span>}
              </> : <>
                <HugeiconsIcon icon={item.group.icon} size={14} strokeWidth={2} className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{item.group.name}</span>
                {isChanged(item.group) && <span className="text-xs text-gray-600">{item.group.value.length} selected</span>}
                <HugeiconsIcon icon={ArrowRight01Icon} size={12} strokeWidth={2} className="h-3 w-3" aria-hidden="true" />
              </>}
            </li>;
          })}
        </ul>
        {items.length === 0 && <p role="status" className="px-3 py-3 oq-text-caption text-gray-600">{term ? 'No matching filters or values' : 'No values available'}</p>}
        {selectedGroup && isChanged(selectedGroup) && <div className="border-t border-gray-100 px-2 pt-1">
          <button type="button" className="btn-ghost btn-toolbar px-2" onClick={() => { selectedGroup.onChange([...(selectedGroup.defaultValue ?? [])]); search.current?.focus(); }}><span className="text-cap-trim">Clear {selectedGroup.name.toLowerCase()}</span></button>
        </div>}
      </div>
    </FloatingFocusManager></FloatingPortal>}
  </>;
}

export function IssueFilters({ searchQuery, onSearchChange, groups, hasFilters, onClear }: {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  groups: IssueFilterGroup[];
  hasFilters: boolean;
  onClear: () => void;
}) {
  const menuButton = useRef<HTMLButtonElement | null>(null);
  return <div className="list-toolbar__filters" role="search" aria-label="Filter issues">
    <div className="relative">
      <label htmlFor="search-issues" className="sr-only">Search issues</label>
      <HugeiconsIcon icon={Search01Icon} size={14} strokeWidth={2} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" aria-hidden="true" />
      <input id="search-issues" type="text" className="input input-toolbar w-48 pl-8" placeholder="Search issues..." value={searchQuery} onChange={event => onSearchChange(event.target.value)} />
    </div>
    {groups.filter(group => group.alwaysVisible || isChanged(group)).map(group => <MultiSelectFilter key={group.name} {...group} selectionSummary
      onChange={value => { group.onChange(value); if (!group.alwaysVisible && value.length === 0) menuButton.current?.focus(); }}
      onClear={isChanged(group) ? () => { group.onChange([...(group.defaultValue ?? [])]); menuButton.current?.focus(); } : undefined} />)}
    <FilterMenu groups={groups} buttonRef={menuButton} />
    {hasFilters && <button type="button" className="btn-ghost btn-toolbar px-2 text-gray-700" onClick={() => { onClear(); menuButton.current?.focus(); }}><span className="text-cap-trim">Clear filters</span></button>}
  </div>;
}
